import { useEffect, useRef, useState } from 'react';

// Reusable searchable site dropdown. Typing filters in real time across
// site number, site name, city, and customer (company) name — built for
// multi-site customers like Centex (85 locations) where a plain <select>
// is unusable.
//
// Props:
//   sites          [{ id, siteNumber, siteName, city, company, displayName }]
//   value          selected site id (number) or '' for none
//   onChange(id)   called with the chosen site id, or '' for internal/none
//   allowInternal  show a "— No site (internal) —" option (default true)
//   placeholder    input placeholder
//   autoFocus      focus the input on mount
export default function SiteSearchSelect({
  sites = [], value, onChange, allowInternal = true, placeholder, autoFocus
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const selected = sites.find((s) => s.id === Number(value)) || null;
  const selectedLabel = selected ? selected.displayName : '';

  useEffect(() => {
    function onDocDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = sites.filter((s) => {
    if (!q) return true;
    return [s.siteNumber, s.siteName, s.city, s.company]
      .some((v) => String(v || '').toLowerCase().includes(q));
  }).slice(0, 50);

  // Internal/none option sits first when allowed.
  const options = allowInternal ? [{ __internal: true }, ...matches] : matches;

  function choose(opt) {
    onChange(opt.__internal ? '' : opt.id);
    setQuery(''); setOpen(false); setHighlight(0);
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setOpen(true);
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && options[highlight]) { e.preventDefault(); choose(options[highlight]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  return (
    <div className="site-combo" ref={rootRef}>
      <input
        type="text"
        autoFocus={autoFocus}
        value={open ? query : selectedLabel}
        placeholder={placeholder || 'Search by site #, name, city, or customer…'}
        onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul className="site-combo-list" ref={listRef}>
          {options.length === 0 && (
            <li className="site-combo-empty"><span className="cell-muted">No matching sites</span></li>
          )}
          {options.map((opt, i) => opt.__internal ? (
            <li
              key="__internal"
              className={i === highlight ? 'site-combo-active' : ''}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
            >
              <span className="cell-muted">— No site (internal) —</span>
            </li>
          ) : (
            <li
              key={opt.id}
              className={i === highlight ? 'site-combo-active' : ''}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
            >
              <span className="cell-primary">{opt.displayName}</span>
              <span className="cell-mono cell-muted">
                {[opt.siteNumber, opt.city, opt.company].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
